/**
 * Iter145 — Rally the Court button with cool-down chip.
 *
 * Shown to booked players (and the coach) when a session has a `min_players`
 * floor and hasn't yet met it. Fires POST /api/offerings/{id}/rally which
 * fans out bell notifications + emails to eligible players in the same
 * region. Rate-limited server-side to 1 rally per user per session per 6h.
 *
 * Iter145 addition: on mount we call GET /api/offerings/{id}/rally-status to
 * pull the current user's cooldown. If they've rallied recently, the button
 * is disabled and shows a "Rallied Xh ago · try again in Yh" chip so nobody
 * wonders why it won't fire.
 */
import { useEffect, useState } from "react";
import { Megaphone, Info } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

function _fmtRelPast(iso) {
  if (!iso) return "";
  const secs = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}
function _fmtRelFuture(seconds) {
  const s = Math.max(0, Math.floor(seconds));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.ceil(s / 60)}m`;
  return `${Math.ceil(s / 3600)}h`;
}

export default function RallyCourtButton({ offering, onRallied }) {
  const [busy, setBusy] = useState(false);
  const [showTip, setShowTip] = useState(false);
  const [cool, setCool] = useState(null); // {cooldown_seconds, last_rallied_at, cooldown_until}

  const refreshStatus = async () => {
    try {
      const { data } = await api.get(`/offerings/${offering.id}/rally-status`);
      setCool(data);
    } catch { setCool(null); }
  };

  useEffect(() => {
    refreshStatus();
    // Countdown tick every 30s so the chip label updates in the background
    // without stalling any parent renders.
    const t = setInterval(() => {
      setCool((c) => (c ? { ...c, cooldown_seconds: Math.max(0, (c.cooldown_seconds || 0) - 30) } : c));
    }, 30_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offering.id]);

  const cooldownActive = !!(cool && cool.cooldown_seconds > 0);
  const rally = async () => {
    if (busy || cooldownActive) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/offerings/${offering.id}/rally`);
      toast.success(
        data?.notified
          ? `Rallied ${data.notified} player${data.notified === 1 ? "" : "s"} — fingers crossed!`
          : "No eligible players to rally right now.",
      );
      refreshStatus();
      if (onRallied) onRallied(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not rally");
      refreshStatus();
    } finally { setBusy(false); }
  };

  return (
    <div className="relative inline-flex items-start gap-1.5 flex-wrap" data-testid={`rally-wrap-${offering.id}`}>
      <div className="flex flex-col gap-1">
        <button
          type="button"
          onClick={rally}
          disabled={busy || cooldownActive}
          data-testid={`rally-btn-${offering.id}`}
          className="min-h-[44px] px-4 py-2.5 text-xs uppercase tracking-wider inline-flex items-center gap-2 border border-[#FF9F1C] text-[#FF9F1C] hover:bg-[#FF9F1C] hover:text-black active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#FF9F1C]"
        >
          <Megaphone className="w-3.5 h-3.5" strokeWidth={2} />
          {busy ? "Rallying…" : "Rally the Court"}
        </button>
        {cooldownActive && (
          <span
            data-testid={`rally-cooldown-${offering.id}`}
            className="text-[10px] uppercase tracking-wider text-[#FF9F1C] leading-tight"
          >
            Rallied {_fmtRelPast(cool.last_rallied_at)} · try again in {_fmtRelFuture(cool.cooldown_seconds)}
          </span>
        )}
      </div>
      <button
        type="button"
        aria-label="What is Rally the Court?"
        onFocus={() => setShowTip(true)}
        onBlur={() => setShowTip(false)}
        onMouseEnter={() => setShowTip(true)}
        onMouseLeave={() => setShowTip(false)}
        data-testid={`rally-info-${offering.id}`}
        className="text-[#A3A3A3] hover:text-[#FF9F1C] transition-colors mt-3"
      >
        <Info className="w-4 h-4" />
      </button>
      {showTip && (
        <div
          role="tooltip"
          data-testid={`rally-tooltip-${offering.id}`}
          className="absolute z-20 left-0 top-full mt-2 w-64 px-3 py-2 text-[11px] leading-relaxed bg-[#1A1A1A] border border-[#FF9F1C] text-[#E5E5E5] shadow-xl"
        >
          Help the session go ahead — fill the court!
        </div>
      )}
    </div>
  );
}
